import {
  Typography,
  Stack,
  Button,
  Switch,
  MenuItem,
  Select,
  Box,
  Divider,
  InputLabel,
  FormControl
} from "@mui/material";

import {
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowCollapse
} from "@qubit/autoparts";
import { t } from "i18next";
import { useCallback, useEffect, useState, Fragment } from "react";

import { useAppSelector, useAppDispatch } from "~/app/store";
import { useFlag } from "~/config/flags";
import {
  selectPongs,
  selectAllPongHistories,
  setAutoPingInterval,
  setPongHistoryTimeWindow,
  selectAutoPingInterval,
  selectPongHistoryTimeWindow,
  selectAutoPingEnabled,
  setAutoPingEnabled,
  clearPongHistory
} from "~/features/andon/andon.slice";

import { PongData } from "~/features/andon/andon.types";
import {
  getMonitorTabSelectedGridId,
  setMonitorTabSelectedGridId
} from "~/features/serviceSupport/serviceSupport.slice";
import { listenAndon, hubs } from "~/lib/signalr";
import {
  selectSignalRReadyAndon,
  selectSignalRReadyMain
} from "~/redux/selectors/appSelectors";
import { selectUsersFulfillmentCenterId } from "~/redux/selectors/storeSelectors";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import { HeartbeatEvent } from "~/types/api";

import { SparkLine } from "./Sparkline";

export function ensureTwoPoints<T>(array: T[]): T[] {
  return array.length === 1 ? [array[0], array[0]] : array;
}

export const getColorForValue = (
  value: number,
  type: "fps" | "rtt" | "downlink"
): "green" | "orange" | "red" => {
  if (type === "fps") {
    if (value >= 50) return "green";
    if (value >= 30) return "orange";
    return "red";
  }
  if (type === "rtt") {
    if (value <= 100) return "green";
    if (value <= 200) return "orange";
    return "red";
  }
  if (type === "downlink") {
    if (value >= 10) return "green";
    if (value >= 3) return "orange";
    return "red";
  }
  return "green";
};

export const getMemoryColor = (memory?: PongData["memory"]) => {
  if (!memory || !memory.usedJSHeapSize || !memory.jsHeapSizeLimit) {
    return undefined;
  }

  const percent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;

  if (percent <= 50) return "green";
  if (percent <= 80) return "orange";
  return "red";
};

export function getMinMaxAvg(data: number[]) {
  if (data.length === 0) return { min: "0", max: "0", avg: "0" };
  const sum = data.reduce((a, b) => a + b, 0);
  return {
    min: Math.min(...data).toFixed(1),
    max: Math.max(...data).toFixed(1),
    avg: (sum / data.length).toFixed(1)
  };
}

export const calculateMemoryUsagePercentage = (memory?: PongData["memory"]) => {
  if (!memory || !memory.usedJSHeapSize || !memory.jsHeapSizeLimit) {
    return "";
  }

  const percent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
  return `${percent.toFixed(1)}`;
};

export function toggleExpandedRow(current: string[], id: string): string[] {
  return current.includes(id)
    ? current.filter((i) => i !== id)
    : [...current, id];
}

export function getActualHistoryDurationMs(
  entries: { timestamp: string }[]
): number {
  const oldest = entries.at(0)?.timestamp;
  const newest = entries.at(-1)?.timestamp;
  return oldest && newest
    ? new Date(newest).getTime() - new Date(oldest).getTime()
    : 0;
}

export const WorkstationMonitorTab = () => {
  const { extendPongData } = useFlag();

  const dispatch = useAppDispatch();

  const selectedGridId = useAppSelector(getMonitorTabSelectedGridId);

  const { data: autostoreGrids = [] } = useGetAutostoreGridsQuery();

  const pongs = useAppSelector(selectPongs);
  const pongHistories = useAppSelector(selectAllPongHistories);
  const autoPingEnabled = useAppSelector(selectAutoPingEnabled);
  const pongHistoryTimeWindow = useAppSelector(selectPongHistoryTimeWindow);

  const usersFulfillmentCenterId = useAppSelector(
    selectUsersFulfillmentCenterId
  );
  const signalRReadyMain = useAppSelector(selectSignalRReadyMain);
  const signalRReadyAndon = useAppSelector(selectSignalRReadyAndon);
  const autoPingInterval = useAppSelector(selectAutoPingInterval);

  const { data: workstations } = useGetWorkstationsQuery();

  const now = Date.now();

  const filteredWorkstations = (workstations ?? []).filter((ws) => {
    const pong = pongs?.[ws.id];
    const pongHistory = pongHistories?.[ws.id] ?? [];
    const timeWindowedHistory = pongHistory.filter(
      (entry) =>
        now - new Date(entry.timestamp).getTime() <= pongHistoryTimeWindow
    );

    // Only show if live pong or history in the current window
    return (
      (!selectedGridId || ws.autostoreGridId === selectedGridId) &&
      (pong || timeWindowedHistory.length > 0)
    );
  });

  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const pongHistoryOptions = [
    { label: "10 seconds", value: 10 * 1000 },
    { label: "30 seconds", value: 30 * 1000 },
    { label: "1 minute", value: 1 * 60 * 1000 },
    { label: "5 minutes", value: 5 * 60 * 1000 },
    { label: "10 minutes", value: 10 * 60 * 1000 },
    { label: "30 minutes", value: 30 * 60 * 1000 }
  ];

  const allHistoryEntries = Object.values(pongHistories).flat();

  const actualHistoryDurationMs = getActualHistoryDurationMs(allHistoryEntries);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => toggleExpandedRow(prev, id));
  };
  useEffect(() => {
    if (signalRReadyMain && usersFulfillmentCenterId && !signalRReadyAndon) {
      // to listen for pongs on the heartbeat hub
      void listenAndon({ usersFulfillmentCenterId, dispatch });
    }
  }, [signalRReadyMain, usersFulfillmentCenterId, signalRReadyAndon, dispatch]);

  const handlePing = useCallback((targetWorkstationId: string) => {
    const pingEvent: HeartbeatEvent.Ping = {
      case: "Ping",
      item: {
        targetWorkstationId,
        timePingSent: new Date()
      }
    };
    hubs.send.next(pingEvent);
  }, []);

  useEffect(() => {
    let intervalId: number | null = null;

    if (autoPingEnabled) {
      intervalId = window.setInterval(() => {
        handlePing("");
      }, autoPingInterval);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoPingEnabled, handlePing, autoPingInterval]);

  return (
    <Stack spacing={2} height={"100%"}>
      {/* Header controls */}
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={3}
        sx={{ mt: 2 }}
      >
        {/* grid selector */}
        <Box>
          <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
            <InputLabel id="grid-selector-label">
              {t("autostore grid")}
            </InputLabel>
            <Select
              labelId="grid-selector-label"
              id="grid-selector"
              value={selectedGridId}
              onChange={(e) =>
                dispatch(setMonitorTabSelectedGridId(e.target.value))
              }
              label={t("autostore grid")}
              renderValue={(selected) =>
                selected ? (
                  autostoreGrids.find((g) => g.autostoreGridId === selected)
                    ?.autostoreGridName
                ) : (
                  <span style={{ color: "#aaa" }}>{t("select a grid...")}</span>
                )
              }
              displayEmpty
            >
              {autostoreGrids.map((grid) => (
                <MenuItem
                  key={grid.autostoreGridId}
                  value={grid.autostoreGridId}
                >
                  {grid.autostoreGridName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!!autostoreGrids.length && !selectedGridId && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ ml: 1, flexShrink: 0 }}
            >
              {t("no autostore grid selected")}
            </Typography>
          )}
        </Box>

        {/* Controls on right */}
        <Box sx={{ display: "flex", alignItems: "center", ml: "auto", gap: 3 }}>
          {/* clear button */}
          <Button
            variant="contained"
            onClick={() => {
              dispatch(clearPongHistory());
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            {t("clear")}
          </Button>

          {/* fetch latest */}
          <Button
            variant="contained"
            onClick={() => handlePing("")}
            sx={{ whiteSpace: "nowrap" }}
          >
            {t("fetch latest")}
          </Button>

          {/* autoping toggle and ping time select */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              px: 2,
              py: 0.5,
              bgcolor: autoPingEnabled
                ? "background.paper"
                : "action.disabledBackground"
            }}
          >
            <InputLabel sx={{ fontSize: ".8rem" }}>{t("auto ping")}</InputLabel>
            <Switch
              checked={autoPingEnabled}
              onChange={(evt) =>
                dispatch(setAutoPingEnabled(evt.target.checked))
              }
              size="small"
            />
            <Select
              size="small"
              value={autoPingInterval}
              onChange={(e) =>
                dispatch(setAutoPingInterval(Number(e.target.value)))
              }
              disabled={!autoPingEnabled}
              sx={{ width: 120, ml: 1 }}
            >
              <MenuItem value={1000}>1 second</MenuItem>
              <MenuItem value={2000}>2 seconds</MenuItem>
              <MenuItem value={3000}>3 seconds</MenuItem>
              <MenuItem value={4000}>4 seconds</MenuItem>
              <MenuItem value={5000}>5 seconds</MenuItem>
              <MenuItem value={10000}>10 seconds</MenuItem>
              <MenuItem value={30000}>30 seconds</MenuItem>
              <MenuItem value={60000}>60 seconds</MenuItem>
              <MenuItem value={90000}>90 seconds</MenuItem>
              <MenuItem value={120000}>2 minutes</MenuItem>
              <MenuItem value={300000}>5 minutes</MenuItem>
            </Select>
          </Box>

          {/* time filter for display */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <InputLabel sx={{ fontSize: ".8rem" }}>
              {t("time filter")}
            </InputLabel>
            <Select
              size="small"
              value={pongHistoryTimeWindow}
              disabled={actualHistoryDurationMs === 0}
              onChange={(e) =>
                dispatch(setPongHistoryTimeWindow(Number(e.target.value)))
              }
              sx={{ width: 160 }}
            >
              {pongHistoryOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={actualHistoryDurationMs < option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Box>
      </Stack>

      {/* workstation data table */}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        <ASTableV2
          gridTemplateColumns={`minmax(200px, 2fr) repeat(${extendPongData ? 5 : 3}, minmax(120px, 1fr))`}
          isLoading={false}
        >
          <ASTableV2Header>
            <ASTableV2Row>
              <ASTableV2Cell>{t("workstation")}</ASTableV2Cell>
              <ASTableV2Cell sx={{ justifyContent: "center" }}>
                {t("downlink")}
              </ASTableV2Cell>
              <ASTableV2Cell sx={{ justifyContent: "center" }}>
                {t("rtt")}
              </ASTableV2Cell>
              {extendPongData && (
                <ASTableV2Cell sx={{ justifyContent: "center" }}>
                  {t("fps")}
                </ASTableV2Cell>
              )}
              {extendPongData && (
                <ASTableV2Cell sx={{ justifyContent: "center" }}>
                  {t("memory")}
                </ASTableV2Cell>
              )}
              <ASTableV2Cell sx={{ justifyContent: "center" }}>
                {t("ping loop time")}
              </ASTableV2Cell>
            </ASTableV2Row>
          </ASTableV2Header>

          <ASTableV2Body>
            {filteredWorkstations.map((ws) => {
              const pong = pongs?.[ws.id];
              const pongData = pong?.pongData;

              const loopTime = pong?.pongLoopTime;

              const downlink = pongData?.network?.downlink;
              const rtt = pongData?.network?.rtt;

              const pongHistory = pongHistories[ws.id] ?? [];

              const now = Date.now();

              const history = pongHistory.filter(
                (entry) =>
                  now - new Date(entry.timestamp).getTime() <=
                  pongHistoryTimeWindow
              );

              const buildMetricCell = ({
                current,
                historyEntries,
                accessor,
                unit,
                colorFunc
              }: {
                current: number | string | undefined;
                historyEntries: {
                  timestamp: string;
                  pongData: PongData;
                  loopTime: number;
                }[];
                accessor: (entry: (typeof historyEntries)[number]) => number;
                unit: string;
                defaultMax: number;
                colorFunc?: (value: number) => string | undefined;
              }) => {
                const data = historyEntries.map(accessor);
                const timestamps = historyEntries.map(
                  (entry) => entry.timestamp
                );

                const stats = getMinMaxAvg(data);

                const hasValidHistory = data.some(
                  (val) => typeof val === "number" && val > 0 && !isNaN(val)
                );
                return (
                  <Stack direction="column" alignItems="center" spacing={0.5}>
                    {/* Sparkline Chart */}
                    {current !== undefined && (
                      <SparkLine
                        data={ensureTwoPoints(data)}
                        timestamps={ensureTwoPoints(timestamps)}
                        yMin={0}
                      />
                    )}

                    {/* Current Value */}
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        textAlign: "center",
                        color: colorFunc
                          ? colorFunc(Number(current) || 0)
                          : undefined
                      }}
                    >
                      {current !== undefined ? `${current} ${unit}` : " "}
                    </Typography>

                    {/* Min/Max/Avg */}
                    {hasValidHistory && (
                      <Typography
                        variant="caption"
                        sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                      >
                        min {stats.min} / max {stats.max} / avg {stats.avg}
                      </Typography>
                    )}
                  </Stack>
                );
              };

              return (
                <Fragment key={ws.id}>
                  <ASTableV2Row
                    selected={expandedRows.includes(ws.id)}
                    onClick={() => toggleRow(ws.id)}
                  >
                    {/* Workstation */}
                    <ASTableV2Cell sx={{ minWidth: 200 }}>
                      <Stack
                        direction="column"
                        spacing={0.75}
                        alignItems="flex-start"
                      >
                        <Typography
                          variant="tableBody"
                          sx={{ minWidth: 180, minHeight: 24 }}
                        >
                          {ws.deviceId || "Placeholder-ID"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            minWidth: 180,
                            minHeight: 18,
                            color: "text.secondary"
                          }}
                        >
                          {pongData?.userAgentParsed
                            ? `${pongData.userAgentParsed.browser || " "} ${pongData.userAgentParsed.version || " "} ${pongData.userAgentParsed.os || " "}`
                            : " "}
                        </Typography>
                      </Stack>
                    </ASTableV2Cell>

                    {/* Downlink */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      {buildMetricCell({
                        current: downlink,
                        historyEntries: history,
                        accessor: (entry) =>
                          entry.pongData.network?.downlink ?? 0,
                        unit: "Mbps",
                        defaultMax: 20,
                        colorFunc: (val) => getColorForValue(val, "downlink")
                      })}
                    </ASTableV2Cell>

                    {/* RTT */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      {buildMetricCell({
                        current: rtt,
                        historyEntries: history,
                        accessor: (entry) => entry.pongData.network?.rtt ?? 0,
                        unit: "ms",
                        defaultMax: 500
                      })}
                    </ASTableV2Cell>

                    {/* FPS */}
                    {extendPongData && (
                      <ASTableV2Cell sx={{ justifyContent: "center" }}>
                        {buildMetricCell({
                          current: pongData?.fps,
                          historyEntries: history,
                          accessor: (entry) => entry.pongData.fps ?? 0,
                          unit: "fps",
                          defaultMax: 60,
                          colorFunc: (val) => getColorForValue(val, "fps")
                        })}
                      </ASTableV2Cell>
                    )}

                    {/* Memory */}
                    {extendPongData && (
                      <ASTableV2Cell sx={{ justifyContent: "center" }}>
                        {buildMetricCell({
                          current: pongData?.memory
                            ? `${calculateMemoryUsagePercentage(pongData.memory)}`
                            : undefined,
                          historyEntries: history,
                          accessor: (entry) => {
                            const mem = entry.pongData.memory;
                            if (mem?.usedJSHeapSize && mem?.jsHeapSizeLimit) {
                              return (
                                Math.round(
                                  (mem.usedJSHeapSize / mem.jsHeapSizeLimit) *
                                    10000
                                ) / 100
                              );
                            }
                            return 0;
                          },
                          unit: "%",
                          defaultMax: 100,
                          colorFunc: () => getMemoryColor(pongData?.memory)
                        })}
                      </ASTableV2Cell>
                    )}

                    {/* Loop Time */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      {buildMetricCell({
                        current: loopTime,
                        historyEntries: history,
                        accessor: (entry) => entry.loopTime ?? 0,
                        unit: "ms",
                        defaultMax: 500
                      })}
                    </ASTableV2Cell>
                  </ASTableV2Row>
                  <ASTableV2RowCollapse
                    expanded={expandedRows.includes(ws.id)}
                    selected={expandedRows.includes(ws.id)}
                  >
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={3}>
                        {/* System Info */}
                        <Stack spacing={1}>
                          <Typography variant="h6">System Info</Typography>
                          <Divider />
                          {[
                            ["CPU cores", pongData?.cpu?.cores ?? ""],
                            [
                              "Network Type",
                              pongData?.network?.effectiveType ?? ""
                            ],
                            ...(extendPongData
                              ? [
                                  [
                                    "Memory used",
                                    pongData?.memory?.usedJSHeapSize ?? ""
                                  ],
                                  [
                                    "Memory total",
                                    pongData?.memory?.totalJSHeapSize ?? ""
                                  ],
                                  [
                                    "Memory limit",
                                    pongData?.memory?.jsHeapSizeLimit ?? ""
                                  ]
                                ]
                              : [])
                          ].map(([label, value]) => (
                            <Stack direction="row" spacing={1} key={label}>
                              <Typography
                                variant="body2"
                                sx={{ minWidth: 180, fontWeight: 500 }}
                              >
                                {label}:
                              </Typography>
                              <Typography variant="body2">{value}</Typography>
                            </Stack>
                          ))}
                        </Stack>

                        {/* Low Entropy UA */}
                        {pongData?.userAgentLowEntropy && (
                          <Stack spacing={1}>
                            <Typography variant="h6">
                              UA Client Hints (Low Entropy)
                            </Typography>
                            <Divider />
                            {[
                              [
                                "Brands",
                                pongData.userAgentLowEntropy.brands
                                  .map((b) => `${b.brand} (${b.version})`)
                                  .join(", ")
                              ],
                              [
                                "Platform",
                                pongData.userAgentLowEntropy.platform
                              ],
                              [
                                "Mobile",
                                pongData.userAgentLowEntropy.mobile
                                  ? "Yes"
                                  : "No"
                              ]
                            ].map(([label, value]) => (
                              <Stack direction="row" spacing={1} key={label}>
                                <Typography
                                  variant="body2"
                                  sx={{ minWidth: 180, fontWeight: 500 }}
                                >
                                  {label}:
                                </Typography>
                                <Typography variant="body2">{value}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        )}

                        {/* High Entropy UA */}
                        {pongData?.userAgentHighEntropy && (
                          <Stack spacing={1}>
                            <Typography variant="h6">
                              UA Client Hints (High Entropy)
                            </Typography>
                            <Divider />
                            {[
                              [
                                "Architecture",
                                pongData.userAgentHighEntropy.architecture ?? ""
                              ],
                              [
                                "Bitness",
                                pongData.userAgentHighEntropy.bitness ?? ""
                              ],
                              [
                                "Model",
                                pongData.userAgentHighEntropy.model ?? ""
                              ],
                              [
                                "Platform",
                                pongData.userAgentHighEntropy.platform ?? ""
                              ],
                              [
                                "Platform Version",
                                pongData.userAgentHighEntropy.platformVersion ??
                                  ""
                              ],
                              [
                                "UA Full Version",
                                pongData.userAgentHighEntropy.uaFullVersion ??
                                  ""
                              ]
                            ].map(([label, value]) => (
                              <Stack direction="row" spacing={1} key={label}>
                                <Typography
                                  variant="body2"
                                  sx={{ minWidth: 180, fontWeight: 500 }}
                                >
                                  {label}:
                                </Typography>
                                <Typography variant="body2">{value}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        )}

                        {/* Full User Agent */}
                        {pongData?.userAgent && (
                          <Stack spacing={1}>
                            <Typography variant="h6">
                              Full User Agent
                            </Typography>
                            <Divider />
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: "pre-wrap" }}
                            >
                              {pongData.userAgent}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </ASTableV2RowCollapse>
                </Fragment>
              );
            })}
          </ASTableV2Body>
        </ASTableV2>
      </Box>
    </Stack>
  );
};

export default WorkstationMonitorTab;
